import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    combineLatest,
    distinctUntilChanged,
    Observable,
    of,
    scan,
    Subject,
    map,
    takeUntil,
    delay,
    catchError,
    defer,
    startWith,
    shareReplay,
} from 'rxjs';
import { Hit } from '@faro/searchapi-angular-client';
import {
    selectHitlist,
    selectIsLoading,
    selectShowHitlist,
    selectShowNoHits,
    selectIsBeforeFirstSearch,
    selectTotalHitCount,
    selectIsLoadingNextPage,
    selectLastResponseSearchType,
} from '../../search-state/search-result.selectors';
import { HitMapperService } from './hit-mapper.service';
import { cancelSearch } from '../../search-state/search-result.actions';
import { selectSearchProfileDefault } from '../../search-state/search-profile.selectors';
import { TableHitComponentModel } from './table-hitlist/table-hit-component.model';
import { DetailsService, KeyframesService, WordSequenceService } from '@faro/metadata-angular-client';
import type { WordSequenceMetadataDto } from '@faro/metadata-angular-client';
import { selectSearchFieldSelection } from '../../search-state/search-options.selectors';
import {
    SEARCH_FILTERS,
    SearchFieldSelectionEnum,
    SearchFieldSelectionOption,
} from '../../search-state/search-options.state';
import { setSearchFieldSelection } from '../../search-state/search-options.actions';
import { composeTopicTooltip } from './topic-tooltip.helper';

@Component({
  standalone: false,
    selector: 'app-hitlist',
    templateUrl: './hitlist.component.html',
    styleUrls: ['./hitlist.component.scss'],
})
export class HitlistComponent implements OnInit, OnDestroy {
    totalHitCount$: Observable<number | undefined>;
    tableHitlist$: Observable<TableHitComponentModel[]>;
    isLoading$: Observable<boolean>;
    isLoadingNextPage: boolean = false;
    areProfileFiltersSet$: Observable<boolean>;
    showNoSearch$: Observable<boolean>;
    showNoHits$: Observable<boolean>;
    hideHitlist$: Observable<boolean>;
    private selectedSearchOption: SearchFieldSelectionOption = { value: SearchFieldSelectionEnum.TOPIC };

    private _destroyed$ = new Subject<void>();
    lastResponseSearchType: SearchFieldSelectionOption = { value: SearchFieldSelectionEnum.TOPIC };

    constructor(
        public readonly store: Store,
        private readonly hitMapper: HitMapperService,
        private readonly keyframeService: KeyframesService,
        private readonly detailsService: DetailsService,
        private readonly wordSequenceService: WordSequenceService
    ) {
        this.store
            .select(selectSearchFieldSelection)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(data => this.selectedSearchOption = data);
            
        this.tableHitlist$ = this.store.select(selectHitlist).pipe(
            distinctUntilChanged(),
            scan((currentModels: TableHitComponentModel[], hitlist: Hit[]) => {
                /** very important to reuse existing models, otherwise keyframe images are re-fetched on lazy loading...
                 * in case a new search is executed the existing models will not be reused. Important for highlighting
                 * */
                const lazyLoaded = hitlist.length > 50;
                if (lazyLoaded) {
                    return hitlist.map((hit, ix) => {
                        const currentModel = currentModels[ix];
                        return currentModel?.hitId === hit.id
                            ? currentModel
                            : this.createHitComponentModel(hit, this.lastResponseSearchType);
                    });
                } else {
                    return hitlist.map(hit => {
                        return this.createHitComponentModel(hit, this.lastResponseSearchType);
                    });
                }
            }, [])
        );

        this.totalHitCount$ = this.store.select(selectTotalHitCount);
        this.isLoading$ = this.store.select(selectIsLoading);
        this.showNoSearch$ = this.store.select(selectIsBeforeFirstSearch);
        this.showNoHits$ = this.store.select(selectShowNoHits);
        this.hideHitlist$ = this.store.select(selectShowHitlist).pipe(map(show => !show));
        this.areProfileFiltersSet$ = this.store.select(selectSearchProfileDefault);

        this.store
            .select(selectLastResponseSearchType)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(data => {
                if (data) {
                     this.lastResponseSearchType = { value: data as SearchFieldSelectionEnum };
                    if (data !== this.selectedSearchOption.value) {
                        const searchFilter = SEARCH_FILTERS.find(v => v.value === data);
                        if (searchFilter) {
                            this.store.dispatch(setSearchFieldSelection({ searchFieldSelection: searchFilter }));
                        }
                    }
                }
            });
    }

    private createHitComponentModel(
        hit: Hit,
        selectedSearchOption: SearchFieldSelectionOption
    ): TableHitComponentModel {
        const model = this.hitMapper.mapHitToTableHitComponentModel(hit, selectedSearchOption);
        const delayInMs = (hit as any).indexOnPage || 0;
        model.keyframeBlob$ =
            model.keyframeId! >= 0
                ? this.keyframeService.keyframesGet(model.keyframeId!).pipe(
                      delay(delayInMs),
                      catchError(_ => of(null))
                  )
                : of(null);

        if (selectedSearchOption.value === SearchFieldSelectionEnum.TOPIC && model.programId && model.itemId) {
            model.topicTooltip$ = this.createTopicTooltipObservable(model);
        }

        return model;
    }

    /**
     * Tooltip of the "Thema" column, composed entirely from metadata service data: the sequences
     * description text (video content only) and the "O-Ton" word sequences of the item. Both are
     * loaded on first subscription — which happens when the row is rendered, because the offscreen
     * screen-reader span of the cell shows the same content — and each section is added as soon as
     * its data arrives. The composed result is cached per row.
     *
     * defer + shareReplay(1) implement the "lazy-once-then-cached" pattern: nothing (not even
     * building the request pipeline) happens until the first subscriber, the metadata service is
     * queried exactly once per row, and every later subscriber (e.g. the tooltip next to the
     * offscreen span) replays the latest composed result instead of re-fetching.
     */
    private createTopicTooltipObservable(model: TableHitComponentModel): Observable<string> {
        return defer(() =>
            combineLatest([
                this.detailsService.detailsGetItemSequencesText(model.programId!, model.itemId!).pipe(
                    map(result => result?.text ?? ''),
                    catchError(_ => of('')),
                    startWith('')
                ),
                this.wordSequenceService.wordSequenceGetItemWordSequences(model.programId!, model.itemId!).pipe(
                    catchError(_ => of([] as WordSequenceMetadataDto[])),
                    startWith([] as WordSequenceMetadataDto[])
                ),
            ])
        ).pipe(
            map(([sequencesText, wordSequences]) => composeTopicTooltip(sequencesText, wordSequences)),
            shareReplay(1)
        );
    }

    ngOnInit() {
        this.store
            .select(selectIsLoadingNextPage)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(data => {
                this.isLoadingNextPage = data;
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    onCancelSearch() {
        this.store.dispatch(cancelSearch());
    }
}
